import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _intersection from 'lodash/intersection';
import { Typography, LazyLoadImage, HoverableIconBtn } from 'ck-ui';
import { getBrandImageUrl, getStoreLocation, scriptTagToHash } from 'ck-utils';
import { getPriceString, getSalePercentage } from 'ck-utils/price';
import { isFutureDateString } from 'ck-utils/dateTime';
import { trackAlgoliaClick, trackAlgoliaRecommendClick, trackProductClick } from 'ck-utils/algoliaAnalytics';
import layout from 'ck-utils/layout.module.scss';
import { DropTimer } from './DropTimer';
import { ProductImage } from './ProductImage';
import { ProductQuickView } from './ProductQuickView';
import { ProductTopSash, InTileFooterSash, BundleIconSash } from '../../../ProductSash';
import { VariantStockIndicator } from '../../../VariantStockIndicator/VariantStockIndicator';
import styles from './ProductHit.module.scss';

class ProductHit extends Component {
  constructor(prop) {
    super(prop);
    const { hit } = prop;
    const region = getStoreLocation().toLowerCase();
    this.tags = [
      ...hit.tags,
      'all-products',
      `all-products-${region}`,
      ...hit.isOnSale ? ['mark-down', `mark-down-${region}`] : []
    ];
    this.hasValidDropIn = isFutureDateString(hit.dropsIn);

    this.inStoreOnly = this.tags.includes('marketing:in-store-only');
    this.isSoldOutButStayInCollection = !hit.inStock && this.tags.includes('marketing:stay-in-collection');
    this.isForcedSoldOut = this.tags.includes('marketing:force-sold-out');
    this.isHiddenTimerComingSoon = this.tags.includes('marketing:coming-soon') && this.hasValidDropIn;

    // If product has dropIn date, check against the date, only allow add to cart when drop has ended
    this.canAddToCart = !this.isForcedSoldOut && !this.hasValidDropIn && !hit.dropPin && !this.inStoreOnly;

    const { isFurtherMarkdown, furtherMarkdownTags, furtherMarkdownFactor, isCouponMessageEnabled, couponMessageTags, couponCode, couponPriceFactor } = window.Shopify.sale;

    // Further mark down
    if ((isFurtherMarkdown && hit.isOnSale) || (furtherMarkdownTags && _intersection(furtherMarkdownTags.split(','), this.tags).length > 0)) {
      this.priceFactor = Number(furtherMarkdownFactor) || 0.8;
      this.showDiscountPrice = true;
    }

    // Script override discount - e.g. 3 for $99
    const currentScriptOverrideHash = this.tags.reduce((acc, tag) => {
      if (tag.includes('script=group_override:')) {
        const scriptOverrideHash = scriptTagToHash(tag);
        const value = Number(scriptOverrideHash.value);
        if (!acc || value > acc.value) {
          acc = scriptOverrideHash;
        }
        if (tag.includes('type=percentage')) {
          this.isMultiBuyPercentageEligible = true;
        }
      }
      return acc;
    }, null);

    if (currentScriptOverrideHash) {
      // Percentage tag
      if (this.isMultiBuyPercentageEligible) {
        this.bottomSashMessage = `Multi Buy - Up To ${currentScriptOverrideHash.value}% Off`;
      } else {
        this.bottomSashMessage = `with ${currentScriptOverrideHash.qty} for $${Number(currentScriptOverrideHash.value) / 100}`;
        this.priceFactor = Number(currentScriptOverrideHash.value) / currentScriptOverrideHash.qty / 100 / hit.price;
      }
    }

    // or coupon discount messaging
    if (isCouponMessageEnabled || (couponMessageTags && _intersection(couponMessageTags.split(','), this.tags).length > 0)) {
      this.priceFactor = Number(couponPriceFactor);
      this.bottomSashMessage = `with code: ${couponCode}`;
      this.showDiscountPrice = true;
    }
  }

  trackAndRedirect = (url) => (event) => {
    event?.preventDefault();
    const { hit, indexName, listName, listId, isProductRecommendation } = this.props;
    if (isProductRecommendation) {
      trackAlgoliaRecommendClick({
        index: indexName,
        eventName: 'recommendation_click',
        objectID: hit.objectID
      });
    } else {
      trackAlgoliaClick({
        index: indexName,
        eventName: 'product_click',
        objectID: hit.objectID,
        queryID: hit.__queryID,
        position: hit.__position
      });
    }
    trackProductClick(hit, url, listName, listId);
  }

  onAddToCart = (variantId) => (event) => {
    const { onAddProduct } = this.props;
    const id = variantId || (event && event.target.value);
    if (onAddProduct) {
      onAddProduct(id);
    }
    // Add To Cart in PLP still counts as a product click, but do not redirect
    this.trackAndRedirect()();
  };

  renderPrice = (hit) => {
    const { price, compareAtPrice, priceVaries, tags } = hit;
    const priceString = getPriceString(price, priceVaries);

    if (this.isForcedSoldOut || this.isSoldOutButStayInCollection || this.inStoreOnly) {
      return (
        <Typography display="block" align="centered" className={styles.price} color="danger">
          {this.inStoreOnly ? 'Offline Only' : 'Sold Out'}
        </Typography>
      );
    }

    if (hit.isOnSale) {
      const originalPriceString = getPriceString(compareAtPrice, priceVaries);

      if (this.showDiscountPrice && this.priceFactor) {
        return (
          <div className={styles.price}>
            <Typography className={layout.marginRight} display="block" color="danger">
              Now {getPriceString(price * this.priceFactor)}
            </Typography>
            <Typography className={layout.marginRight} strikeThrough display="inline" color="warning">
              Was {priceString}
            </Typography>
            <Typography strikeThrough display="inline">
              {originalPriceString}
            </Typography>
          </div>
        );
      }

      return (
        <div className={styles.price}>
          <Typography className={layout.marginRight} display="inline" color="danger">
            {tags.indexOf('final-sale') === -1 ? 'Sale' : 'Final Sale'}&nbsp;&nbsp;{priceString}
          </Typography>
          <Typography strikeThrough display="inline">
            {originalPriceString}
          </Typography>
        </div>
      );
    }

    if (this.showDiscountPrice && this.priceFactor) {
      return (
        <div className={styles.price}>
          <Typography className={layout.marginRight} display="inline" color="danger">
            Now&nbsp;&nbsp;{getPriceString(price * this.priceFactor)}
          </Typography>
          <Typography strikeThrough display="inline">
            {priceString}
          </Typography>
        </div>
      );
    }

    return (
      <Typography display="block" align="centered" className={styles.price}>
        {priceString}
      </Typography>
    );
  };

  renderHoverSize = (hit) => {
    const { variants } = hit;
    const { isBundle, onAddProduct } = this.props;

    if (!variants.length) return null;

    // logic to determine if addToCart text should be visible on top
    const addCartVisible = !variants.length >= 20;
    const { canAddToCart } = this;

    return (
      <div className="size-hover-selector">
        {addCartVisible ? (
          <div className="size-text-label">
            ADD TO
            <br />
            {isBundle ? 'BUNDLE' : 'CART'} +
          </div>
        ) : null}

        {variants.map((variant) => (
          <Typography
            renderAs="div"
            color="black-bis"
            key={variant.id}
            variant="body-small"
            strikeThrough={!variant.availableForSale || !canAddToCart}
            onClick={this.onAddToCart(variant.id.replace('gid://shopify/ProductVariant/', ''))}
            data-variant-id={variant.id.replace('gid://shopify/ProductVariant/', '')}
            className={classnames('flex', styles.sizeText, {
              [styles.sizeNotAvailable]: !variant.availableForSale || !canAddToCart,
              'is-sold-out': !variant.availableForSale || !canAddToCart,
              'js-size-button': !onAddProduct,
            })}
          >
            {variant.title}
            <VariantStockIndicator
              isInStock={variant.availableForSale}
              isGiftCard={hit.isGiftCard}
              quantityAvailable={variant.inventoryQuantity}
            />
          </Typography>
        ))}
      </div>
    );
  };

  renderSizeSelector = (hit) => {
    const { variants, priceVaries } = hit;
    if (!variants.length) return null;

    const { isBundle, selectedVariantId, onAddProduct, onDeleteProduct } = this.props;

    if (selectedVariantId) {
      return (
        <div className="size-selector">
          <button
            type="button"
            className={classnames('button', 'primary', 'fullwidth-all', 'color-success-dark')}
            onClick={onDeleteProduct}
          >
            <span className="prefix">
              <span className="icon icon-cross" focusable="false" aria-hidden="true" />
            </span>
            <span className="btn-text">
              {this.props.isMobile ? 'REMOVE' : `REMOVE FROM ${isBundle ? 'BUNDLE' : 'CART'}`}
            </span>
          </button>
        </div>
      );
    }

    const { canAddToCart } = this;

    if (variants.length === 1) {
      const variant = variants[0];
      const variantId = variant.id.replace('gid://shopify/ProductVariant/', '');
      return (
        <div className="size-selector">
          <button
            type="button"
            className={classnames('button', 'primary', 'fullwidth-all', {
              disabled: !variant.availableForSale || !canAddToCart,
              'js-size-button': !onAddProduct,
            })}
            data-variant-id={variantId}
            onClick={this.onAddToCart(variantId)}
            disabled={!variant.availableForSale || !canAddToCart}
          >
            <span className="btn-text">ADD TO {isBundle ? 'BUNDLE' : 'CART'}</span>
          </button>
        </div>
      );
    }

    return (
      <div className="size-selector">
        <select
          aria-label="Select size"
          className={classnames('size-select', 'selector-round', {
            'is-sold-out': !hit.inStock || !canAddToCart,
            'js-size-select': !onAddProduct,
          })}
          onChange={this.onAddToCart()}
          value={isBundle ? '' : undefined} // don't want to change the display value of select in bundle
        >
          <option className="blank" value="">
            ADD TO {isBundle ? 'BUNDLE' : 'CART'}
          </option>
          {variants.map((variant) => {
            const quantity = variant.inventoryQuantity;
            return (
              <option
                key={variant.id}
                value={variant.id.replace('gid://shopify/ProductVariant/', '')}
                disabled={!variant.availableForSale || !canAddToCart}
              >
                {variant.title}
                {priceVaries ? ` - ${getPriceString(variant.price)}` : null}
                {/* eslint-disable-next-line no-nested-ternary */}
                {variant.availableForSale
                // eslint-disable-next-line no-nested-ternary
                  ? quantity >= 10 || hit.isGiftCard ? null
                    : quantity < 5 ? ` - ${quantity} Item${quantity > 1 ? 's' : ''} Left`
                      : ' - Low Stock' : ' - SOLD OUT'}
              </option>
            );
          })}
        </select>
      </div>
    );
  };

  renderInTileFooterSash = () => {
    if (this.inStoreOnly) {
      return <InTileFooterSash bgColor="#B09A5E" text="OFFLINE ONLY" />;
    }

    if (this.isForcedSoldOut || this.isSoldOutButStayInCollection) {
      return <InTileFooterSash bgColor="#D40000" text={this.isForcedSoldOut ? 'BACK SOON - SIGN UP' : 'SOLD OUT'} />;
    }

    if (this.isHiddenTimerComingSoon) {
      return <InTileFooterSash bgColor="#b09a5e" text="COMING SOON" />;
    }

    return <InTileFooterSash tags={this.tags} />;
  };

  renderProductImage = () => {
    const { hit, isMobile, showSingleImage } = this.props;
    if (!hit.image || !hit.images?.length) {
      return <ProductImage image="no-image.gif" title="no image" />;
    }

    if (showSingleImage) {
      return <ProductImage image={hit.image} title={hit.title} />;
    }

    return hit.images.filter(img => img.indexOf('lookbuilder') === -1).slice(0, isMobile ? 5 : 2).map((img) => (
      <ProductImage key={img} image={img} title={hit.title} />
    ));
  };

  render = () => {
    const {
      hit,
      onAddProduct,
      isMobile,
      isBundle,
      canClickThrough,
      showAddToCartAtBottom,
      showSash,
      showQuickView,
      handleDeleteFromWishlist,
      addedToWishList,
      wishlistHasLoaded,
      wishListCollections,
      listName,
      listId,
    } = this.props;
    const { hasValidDropIn, isHiddenTimerComingSoon } = this;
    const handles = hit.collectionHandles?.filter(
      (handle) => handle.indexOf('-look') !== -1 || handle.indexOf('-bundle') !== -1
    );
    const clickThroughUrl = canClickThrough ? `/products/${hit.handle}` : null;
    const isDeleteBtn = Boolean(handleDeleteFromWishlist);
    return (
      <div className={styles.root}>
        <div
          className={classnames(styles.content, {
            'product-tile-container': !isMobile && hit.inStock && !this.isForcedSoldOut,
          })}
        >
          {/* Image Slider */}
          <div className="is-relative">
            {hasValidDropIn && !isHiddenTimerComingSoon && (
              <DropTimer
                dropsIn={hit.dropsIn}
                callOut={
                  this.tags.indexOf('raffle_drop') !== -1 ? (
                    <span style={{ color: '#B09A5E' }}>Raffle Drawn In:</span>
                  ) : (
                    'Drops In:'
                  )
                }
              />
            )}
            <a title={hit.title} className={styles.slider} onClick={this.trackAndRedirect(clickThroughUrl)} href={clickThroughUrl}>
              {this.renderProductImage()}
            </a>
            {!isMobile && <div className="size-hover-wrapper">{this.renderHoverSize(hit)}</div>}
            {showSash && <ProductTopSash tags={this.tags} />}
            {showSash && (!hasValidDropIn || isHiddenTimerComingSoon) && this.renderInTileFooterSash(hit)}
            <div className={classnames(styles.topRightCorner, 'is-flex is-flex-column is-items-end')}>
              <HoverableIconBtn
                text="Wishlist"
                disabled={!(wishlistHasLoaded || isDeleteBtn)}
                iconColor={!(wishlistHasLoaded || isDeleteBtn) ? 'grey-dark' : 'primary'}
                iconName={addedToWishList || isDeleteBtn ? 'wishlist-on' : 'wishlist-off'}
                onClick={isDeleteBtn ? handleDeleteFromWishlist : () => {
                  window.dispatchEvent(new CustomEvent('showWishlistPopup', {
                    detail: {
                      product: {
                        variants: hit.variants,
                        id: `${hit.id}`,
                        image: hit.image,
                        title: hit.shortTitle || hit.title,
                        handle: hit.handle,
                        price: hit.price,
                        brand: hit.vendor,
                        category: hit.productType,
                        listName,
                        listId,
                      },
                      wishListCollections
                    }
                  }));
                }}
              />
              {showSash && handles.length > 0 && !showQuickView && <BundleIconSash handles={handles} productHandle={hit.handle} />}

              {showQuickView && (
                <ProductQuickView
                  handle={hit.handle}
                  isForcedSoldOut={this.isForcedSoldOut}
                  isBundle={isBundle}
                  onAddToCart={this.onAddToCart}
                  isMobile={isMobile}
                  isAddToProgress={!!onAddProduct}
                />
              )}
            </div>
          </div>
          {/* product brand, title, price */}
          <div className={isMobile ? layout.marginTop : layout.marginTopMiddle}>
            <LazyLoadImage
              src={getBrandImageUrl(hit.vendor, 'centre')}
              className={classnames(styles.brand, layout.marginBottomSmall)}
              alt={hit.vendor}
            />
            <Typography noWrap color="black-bis" display="block" align="centered" weight={700} capitalize>
              <a href={clickThroughUrl} onClick={this.trackAndRedirect(clickThroughUrl)}>
                {hit.shortTitle || hit.title}
              </a>
            </Typography>
            {this.renderPrice(hit)}
            {showSash && (
              <>
                {(hit.isOnSale || this.priceFactor || this.isMultiBuyPercentageEligible) && (
                  <div className="mt-1 is-flex-centered">
                    {!this.isMultiBuyPercentageEligible && (
                      <span className="sash-text has-background-danger has-text-white">
                        Save{' '}
                        {
                          this.priceFactor
                            ? getSalePercentage(hit.price * this.priceFactor, hit.isOnSale ? hit.compareAtPrice : hit.price)
                            : getSalePercentage(hit.price, hit.compareAtPrice)
                        }%
                      </span>
                    )}
                    {this.bottomSashMessage && <Typography color="danger" style={{ marginLeft: '6px' }}>{this.bottomSashMessage}</Typography>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {(showAddToCartAtBottom || isMobile) && (
          <div className="product-tile-add-to-cart">{this.renderSizeSelector(hit)}</div>
        )}
      </div>
    );
  };
}

ProductHit.propTypes = {
  hit: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  selectedVariantId: PropTypes.string,
  onDeleteProduct: PropTypes.func,
  onAddProduct: PropTypes.func,
  showSingleImage: PropTypes.bool,
  showAddToCartAtBottom: PropTypes.bool,
  canClickThrough: PropTypes.bool,
  showSash: PropTypes.bool,
  showQuickView: PropTypes.bool,
  isBundle: PropTypes.bool,
  indexName: PropTypes.string.isRequired,
  listName: PropTypes.string.isRequired,
  listId: PropTypes.string,
  addedToWishList: PropTypes.bool,
  handleDeleteFromWishlist: PropTypes.func,
  wishlistHasLoaded: PropTypes.bool.isRequired,
  wishListCollections: PropTypes.array,
  isProductRecommendation: PropTypes.bool,
};

ProductHit.defaultProps = {
  selectedVariantId: null,
  onDeleteProduct: null,
  onAddProduct: null,
  showSingleImage: false,
  showAddToCartAtBottom: false,
  canClickThrough: true,
  showSash: true,
  showQuickView: false,
  isBundle: false,
  addedToWishList: false,
  wishListCollections: [],
  handleDeleteFromWishlist: null,
  isProductRecommendation: false,
  listId: window.location.pathname.split('/').pop(),
};

export { ProductHit };
